// 演出特效 Spine 骨骼載入與工廠 — 沿用 Unity 來源 Art/Animaton 的 Spine 4.2.43 資產。
// 動畫名皆取自 Unity LandscapeUI.prefab 的 GamePlayUI/ReelController 序列化欄位（第一手核對，非猜測）：
//   - ani_preWin：開轉「會中免費遊戲」的預告特效（_preWinAnimation="animation"，機率 _showPreWinRatio=0.6）。
//   - ani_draw：停輪聽牌 Omen 光柱（_omenSpines，動畫 "draw_down" loop，配 se_draw loop）。
//   - ani_reel_bg：消除補牌時的補牌預兆底光（_fillOmenSpines，動畫 "reel_bg" loop）。
//   - ani_wildFrame：Wild 變身時蓋在盤面上的金木框（_changeAnimation，"in"/"out"）。
//   - ani_fly：Scatter 收集／獎勵發放的子彈（_pointAnimation="animation"，速度 2300px/s、Ease InQuad）。
//   - ani_trans：免費遊戲等級過場字卡（_transLvAnimations = lv_1_L/lv_2_L/lv_3_L/lv_max，配 se_pk_{level}）。
//   - ani_multiplier：FG 倍率牌（_multiplierInAnimation="in"、_multiplierChangeAnimation="change"）。
//   - ani_multiplierAdd：倍率牌旁的加成爆點（_addMultiplierAnimation="multiplier"；另含 X3/X5/3spins/8spins）。
//   - ani_freespins：FG「+N 次數」爆點特效（GamePlayUI._freeSpinSpin／SpawnFreeSpinBullet，僅一個動畫
//     "animation"，FS_00~FS_30 翻頁為罐頭特效、Unity 不選頁）。注意：不是進場字卡——FreeSpinsUI 字卡實際用
//     ani_cutscene 的 cutscene_in_L/loop_L（LandscapeUI.prefab 覆寫），見 RewardSpine.ts／RewardOverlayView。
//     retrigger 子彈落定 SPIN 鈕後由 FxLayerView.playBurst 播放。
//   - ani_loading：**開幕紅幕**（GamePlayUI._openingAnimation="ani_loading"）——名稱易誤導，它不是 loading
//     頁的動畫，而是按下 Continue 後蓋住主遊戲畫面再拉開的紅色劇院布幕（1.0s）。首屏必須就緒（Continue
//     之後立刻播，那時 deferred 批次還沒開始載），故留在 core。見 view/OpeningCurtainView.ts。
//   - ani_bg：主/FG 動態背景＋野狼探頭（BackgroundView）；ani_collectSS：收集爪槽（CollectorView）；
//     ani_collectWW／ani_H2~H4 change_1x1：WildInfo 面板（WildInfoView）；ani_score2：主倍率落定爆點
//     （MultiplierView）；ani_reel_glow：FG 進場盤面金光（動畫名即 L/P，FxLayerView.playReelGlow）。
// 骨骼為二進位 .skel、圖集 .atlas（pma:true），已由 Unity .skel.bytes/.atlas.txt 去後綴複製至 public/spine/。

import { Assets } from 'pixi.js'
import { Spine } from '@esotericsoftware/spine-pixi-v8'
import { isSpinePrimed, primeSpineAssets } from '../../framework/assets/spineBundle'

const BASE = '/spine'

interface EffectSpineDef {
  readonly skel: string
  readonly atlas: string
}

const DEFS = {
  preWin: { skel: `${BASE}/preWin/ani_preWin.skel`, atlas: `${BASE}/preWin/ani_preWin.atlas` },
  draw: { skel: `${BASE}/draw/ani_draw.skel`, atlas: `${BASE}/draw/ani_draw.atlas` },
  reelBg: { skel: `${BASE}/reelBg/ani_reel_bg.skel`, atlas: `${BASE}/reelBg/ani_reel_bg.atlas` },
  wildFrame: { skel: `${BASE}/wildframe/ani_wildFrame.skel`, atlas: `${BASE}/wildframe/ani_wildFrame.atlas` },
  fly: { skel: `${BASE}/fly/ani_fly.skel`, atlas: `${BASE}/fly/ani_fly.atlas` },
  trans: { skel: `${BASE}/trans/ani_trans.skel`, atlas: `${BASE}/trans/ani_trans.atlas` },
  multiplier: { skel: `${BASE}/multiplier/ani_multiplier.skel`, atlas: `${BASE}/multiplier/ani_multiplier.atlas` },
  multiplierAdd: { skel: `${BASE}/multiplierAdd/ani_multiplierAdd.skel`, atlas: `${BASE}/multiplierAdd/ani_multiplierAdd.atlas` },
  freespins: { skel: `${BASE}/freespins/ani_freespins.skel`, atlas: `${BASE}/freespins/ani_freespins.atlas` },
  bg: { skel: `${BASE}/bg/ani_bg.skel`, atlas: `${BASE}/bg/ani_bg.atlas` },
  collectSS: { skel: `${BASE}/collect/ani_collectSS.skel`, atlas: `${BASE}/collect/ani_collect.atlas` },
  collectWW: { skel: `${BASE}/collect/ani_collectWW.skel`, atlas: `${BASE}/collect/ani_collect.atlas` },
  score2: { skel: `${BASE}/score/ani_score2.skel`, atlas: `${BASE}/score/ani_score.atlas` },
  reelGlow: { skel: `${BASE}/reelGlow/ani_reel_glow.skel`, atlas: `${BASE}/reelGlow/ani_reel_glow.atlas` },
  opening: { skel: `${BASE}/loading/ani_loading.skel`, atlas: `${BASE}/loading/ani_loading.atlas` },
} as const satisfies Record<string, EffectSpineDef>

export type EffectSpineKey = keyof typeof DEFS

/** 動畫名（來源見檔頭）。 */
export const EFFECT_ANIM = {
  preWin: 'animation',
  omenDraw: 'draw_down',
  fillOmen: 'reel_bg',
  wildFrameIn: 'in',
  wildFrameOut: 'out',
  bullet: 'animation',
  multiplierIn: 'in',
  multiplierChange: 'change',
  multiplierAddBurst: 'multiplier',
  /** 免費遊戲等級過場（index = level-1；Lv4 用 lv_max）。 */
  levelTrans: ['lv_1_L', 'lv_2_L', 'lv_3_L', 'lv_max'],
  /** ani_bg 動態背景（LandscapeUI.prefab MGBackground/FGBackground 的 startingAnimation）。 */
  bgMain: 'mg',
  bgFreeGame: 'fg',
  /** 野狼探頭（GamePlayUI._wolfAnimation="mg_Woif_L"；直版變體 mg_Woif_P）。 */
  bgWolf: 'mg_Woif_L',
  bgWolfPortrait: 'mg_Woif_P',
  /** 收集爪槽（GamePlayUI._pointIn/Loop/OutAnimation，骨骼=ani_collectSS）。 */
  collectPointIn: 'appear',
  collectPointLoop: 'loop',
  collectPointOut: 'fire_disappear',
  /** WildInfo 計數點（GamePlayUI._pointAnimation，骨骼=ani_collectWW）。 */
  wildInfoPoint: 'animation',
  /** WildInfo 頭像轉化（GamePlayUI._h2/h3/h4ChangeAnimation，骨骼=ani_H2/H3/H4）。 */
  wildInfoChange: 'change_1x1',
  /** 主倍率落定爆點（GamePlayUI._scoreSpinAnimation，骨骼=ani_score2）。 */
  scoreWin: 'win',
  /** retrigger「+N」爆點（GamePlayUI._freeSpinAnimation，骨骼=ani_freespins）。 */
  freeSpinBurst: 'animation',
  /** FG 進場盤面金光（GamePlayUI._reelGlowAnimation，骨骼=ani_reel_glow；動畫名即 L/P 橫直變體）。 */
  reelGlowLandscape: 'L',
  reelGlowPortrait: 'P',
  /** 免費遊戲等級過場直版變體（PoriraitUI 覆寫 lv_1_P/lv_2_P/lv_3_P；lv_max 兩版共用）。 */
  levelTransPortrait: ['lv_1_P', 'lv_2_P', 'lv_3_P', 'lv_max'],
  /**
   * 開幕紅幕（GamePlayUI._openingAnimation="ani_loading"，與骨骼同名；該骨骼**只有這一支**動畫、
   * 時長 1.0s、無 L/P 變體）。詳見 view/OpeningCurtainView.ts 檔頭。
   */
  opening: 'ani_loading',
} as const

/** Unity 子彈速度（設計座標 px/s，LandscapeUI.prefab _bulletSpeed）。 */
export const BULLET_SPEED_DESIGN = 2300

/**
 * 只在免費遊戲／報獎才用得到的特效——不進首屏，由 main.ts 於 loading 淡出後背景載入，
 * 呼叫端（FxLayerView）在播放前 `await loadDeferredEffectSpines()`。
 * ⚠️ 這份清單對應 `scripts/pack-spine-bundle.mjs` 的 `DEFERRED_DIRS`（同一批目錄），改動要一起改；
 *    不一致時 DEV 下 spineBundle 會在 console 印警告。
 */
const DEFERRED_KEYS = ['trans', 'freespins', 'fly', 'reelGlow'] as const satisfies readonly EffectSpineKey[]

const isDeferred = (key: string): boolean => (DEFERRED_KEYS as readonly string[]).includes(key)

/** 載入指定特效（allSettled：單一資產失敗不擋其餘特效，工廠回 null 時呼叫端走 fallback）。 */
async function loadKeys(keys: readonly EffectSpineKey[]): Promise<void> {
  const groups = keys.map((key) => DEFS[key])
  await primeSpineAssets(groups)
  // 去重註冊：collectSS/collectWW、score/score2 共用同一份圖集，重複 Assets.add 會觸發 overwrite 警告。
  const registered = new Set<string>()
  const missing = groups.filter((g) => !isSpinePrimed(g))
  for (const g of missing) {
    for (const src of [g.skel, g.atlas]) {
      if (!registered.has(src)) {
        registered.add(src)
        Assets.add({ alias: src, src })
      }
    }
  }
  await Promise.allSettled(missing.map((g) => Assets.load([g.skel, g.atlas])))
}

let coreJob: Promise<void> | null = null
let deferredJob: Promise<void> | null = null

/** 載入首屏（主遊戲）用得到的演出特效骨骼。 */
export function loadEffectSpines(): Promise<void> {
  coreJob ??= loadKeys((Object.keys(DEFS) as EffectSpineKey[]).filter((key) => !isDeferred(key)))
  return coreJob
}

/** 載入免費遊戲／報獎專用特效（可重複呼叫，同一份任務）。 */
export function loadDeferredEffectSpines(): Promise<void> {
  deferredJob ??= loadKeys(DEFERRED_KEYS)
  return deferredJob
}

/** 建立指定特效 Spine；資產缺失/未載入時回 null（呼叫端 fallback 或略過）。 */
export function createEffectSpine(key: EffectSpineKey): Spine | null {
  const def = DEFS[key]
  try {
    return Spine.from({ skeleton: def.skel, atlas: def.atlas })
  } catch {
    return null
  }
}

/** 骨骼是否含指定動畫。 */
export function hasAnimation(spine: Spine, name: string): boolean {
  return spine.skeleton.data.findAnimation(name) !== null
}

/** 指定動畫的實際長度（秒）；不存在回 fallback。 */
export function animationDuration(spine: Spine, name: string, fallback = 0.6): number {
  return spine.skeleton.data.findAnimation(name)?.duration ?? fallback
}
